import React, { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'

interface Step {
  title: string
  description: string
  target?: string // CSS selector for spotlight
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center'
}

const staffSteps: Step[] = [
  {
    title: 'Welcome to Inkcredible Portal',
    description: 'Your all-in-one workspace for managing artwork jobs, tracking performance, and collaborating with your team. Let\'s take a quick tour!',
    position: 'center'
  },
  {
    title: 'Your Dashboard',
    description: 'This is your home base. See today\'s job snapshot, your performance stats, leaderboard ranking and daily target progress at a glance.',
    target: '[href="/dashboard"]',
    position: 'right'
  },
  {
    title: 'Jobs Page',
    description: 'All artwork jobs live here. You\'ll find jobs assigned to you, track their progress, start timers, and submit completed work to QC.',
    target: '[href="/jobs"]',
    position: 'right'
  },
  {
    title: 'Performance Page',
    description: 'Track your credits, efficiency percentage, streaks and daily target achievement. Your hard work is always visible here.',
    target: '[href="/performance"]',
    position: 'right'
  },
  {
    title: 'Notifications',
    description: 'Stay updated with job assignments, QC decisions and important announcements. Click the bell to see all your notifications.',
    target: 'button[title*="notification"], button.relative.p-2',
    position: 'bottom'
  },
  {
    title: 'InkConnect Chat',
    description: 'Chat with your team directly in the portal. Use it to ask questions, share updates or coordinate on jobs.',
    position: 'center'
  },
  {
    title: 'You\'re all set!',
    description: 'That\'s the quick tour! Explore the portal at your own pace. If you ever need help, reach out to your team lead. Happy creating!',
    position: 'center'
  }
]

const clientSteps: Step[] = [
  {
    title: 'Welcome to Your Client Portal',
    description: 'Track your artwork jobs, submit new requests, and see real-time updates — all in one place. Let\'s get you started!',
    position: 'center'
  },
  {
    title: 'Your Dashboard',
    description: 'See your active jobs, subscription usage, and completed work at a glance. Your overview is always just one click away.',
    target: '[href="/dashboard"]',
    position: 'right'
  },
  {
    title: 'Submit & Track Jobs',
    description: 'Click here to see all your jobs, submit new artwork requests, and track their progress in real-time.',
    target: '[href="/jobs"]',
    position: 'right'
  },
  {
    title: 'Rate Completed Jobs',
    description: 'After a job is completed, you can rate the work and leave feedback. Your input helps us improve!',
    position: 'center'
  },
  {
    title: 'Notifications',
    description: 'Get notified when your jobs are completed, need attention, or when your subscription is about to expire.',
    target: 'button.relative.p-2',
    position: 'bottom'
  },
  {
    title: 'You\'re all set!',
    description: 'That\'s everything you need to know! Submit your first job and we\'ll get started right away. Welcome aboard!',
    position: 'center'
  }
]

interface Props {
  role: string
  onComplete: () => void
}

export default function OnboardingTour({ role, onComplete }: Props) {
  const [step, setStep] = useState(0)
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null)
  const [visible, setVisible] = useState(false)

  const steps = role === 'client' ? clientSteps : staffSteps
  const current = steps[step]
  const isLast = step === steps.length - 1
  const isCenter = current.position === 'center' || !current.target

  useEffect(() => {
    setTimeout(() => setVisible(true), 100)
  }, [])

  useEffect(() => {
    if (current.target) {
      const el = document.querySelector(current.target)
      if (el) setTargetRect(el.getBoundingClientRect())
      else setTargetRect(null)
    } else {
      setTargetRect(null)
    }
  }, [step])

  const handleNext = () => {
    if (isLast) {
      handleComplete()
    } else {
      setStep(s => s + 1)
    }
  }

  const handleComplete = () => {
    setVisible(false)
    setTimeout(onComplete, 300)
  }

  // Calculate tooltip position
  const getTooltipStyle = (): React.CSSProperties => {
    if (!targetRect || isCenter) {
      return {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 10001
      }
    }
    const margin = 16
    const tooltipW = 320
    const tooltipH = 200

    switch (current.position) {
      case 'right':
        return {
          position: 'fixed',
          top: Math.min(targetRect.top, window.innerHeight - tooltipH - margin),
          left: Math.min(targetRect.right + margin, window.innerWidth - tooltipW - margin),
          zIndex: 10001
        }
      case 'bottom':
        return {
          position: 'fixed',
          top: targetRect.bottom + margin,
          left: Math.min(
            window.innerWidth - tooltipW - margin,
            Math.max(margin, targetRect.left - tooltipW / 2 + targetRect.width / 2)
          ),
          zIndex: 10001
        }
      case 'top':
        return {
          position: 'fixed',
          top: targetRect.top - tooltipH - margin,
          left: Math.max(margin, targetRect.left - tooltipW / 2 + targetRect.width / 2),
          zIndex: 10001
        }
      default:
        return {
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 10001
        }
    }
  }

  return createPortal(
    <div className={`transition-opacity duration-300 ${visible ? 'opacity-100' : 'opacity-0'}`}>
      {/* Overlay */}
      <div className="fixed inset-0 z-[10000] bg-black/40" />

      {/* Spotlight */}
      {targetRect && !isCenter && (
        <div
          className="fixed z-[10001] rounded-xl ring-4 ring-orange-400 pointer-events-none transition-all duration-300"
          style={{
            top: targetRect.top - 6,
            left: targetRect.left - 6,
            width: targetRect.width + 12,
            height: targetRect.height + 12,
          }}
        />
      )}

      {/* Tooltip */}
      <div
        className="fixed z-[10001] w-80 bg-white dark:bg-[#1c3a5e] rounded-2xl shadow-2xl p-6 transition-all duration-300"
        style={getTooltipStyle()}>

        {/* Step indicator */}
        <div className="flex items-center gap-1 mb-4">
          {steps.map((_, i) => (
            <div key={i} className={`h-1 rounded-full transition-all duration-300 ${i === step ? 'bg-orange-500 w-6' : i < step ? 'bg-orange-200 w-3' : 'bg-gray-200 dark:bg-gray-700 w-3'}`} />
          ))}
        </div>

        <h3 className="font-display font-bold text-navy-900 dark:text-white text-lg mb-2">{current.title}</h3>
        <p className="text-sm text-gray-500 dark:text-gray-300 leading-relaxed mb-5">{current.description}</p>

        <div className="flex items-center justify-between">
          <button onClick={handleComplete} className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
            Skip tour
          </button>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <button onClick={() => setStep(s => s - 1)}
                className="text-xs px-3 py-1.5 rounded-xl border border-gray-200 dark:border-[#1e3a5f] text-gray-500 hover:bg-gray-50 dark:hover:bg-[#1a3050] transition-colors">
                Back
              </button>
            )}
            <button onClick={handleNext}
              className="text-xs px-4 py-1.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-semibold transition-colors">
              {isLast ? 'Get Started!' : 'Next →'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
